import Link from "next/link";
import { Star } from "lucide-react";
import { SiGoogle } from "@icons-pack/react-simple-icons";
import { Card, CardContent } from "@/components/ui/card";
import { ReviewAvatar } from "./ReviewAvatar";
import type { DisplayReview } from "@/lib/review-wall";

/**
 * The review card, shared by the `/reviews` wall and the home carousel so the
 * two surfaces cannot drift. Server-safe (no client state) — the carousel wraps
 * it in a client scroller.
 */

/**
 * Always renders FIVE stars, filling to the rounded rating.
 *
 * The home page used to render `Array.from({ length: review.stars })`, i.e.
 * only the filled ones — so a 4-star review was indistinguishable from a
 * 5-star at a glance, and a rating of 0 rendered an empty row while still
 * announcing "0 out of 5 stars" to a screen reader.
 */
export function StarRow({
  rating,
  label,
  className = "h-4 w-4",
}: {
  rating: number;
  label?: string;
  className?: string;
}) {
  const rounded = Math.round(rating);
  return (
    <div className="flex gap-0.5" role="img" aria-label={label ?? `${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`${className} ${i < rounded ? "fill-gold text-gold" : "text-muted-foreground/30"}`}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

/**
 * Reviewer avatar: the real photo when present and loadable, else an on-brand
 * initial circle. It lives in its own module because it is the one part of the
 * card that needs client state (a remote photo can fail AFTER render, and this
 * card is otherwise server-safe) — see `./ReviewAvatar` for why. Re-exported
 * here so existing importers keep working.
 */
export { ReviewAvatar };

export function ReviewCard({
  review,
  className,
  compact = false,
}: {
  review: DisplayReview;
  className?: string;
  /** Clamp the quote (carousel use) so one long review can't stretch a whole
   *  row of cards. The full text always renders unclamped on /reviews. */
  compact?: boolean;
}) {
  return (
    <Card className={`border-0 shadow-sm ${className ?? ""}`}>
      <CardContent className="flex h-full flex-col p-6 sm:p-7">
        <div className="flex items-start justify-between gap-3">
          {review.rating > 0 ? (
            <StarRow rating={review.rating} label={`${review.rating} out of 5 stars`} />
          ) : (
            <span />
          )}
          {review.source === "google" && (
            <SiGoogle
              size={16}
              color="currentColor"
              className="mt-0.5 shrink-0 text-muted-foreground/70"
              title="Posted on Google"
              aria-label="Posted on Google"
            />
          )}
        </div>
        <blockquote
          className={`mt-4 flex-1 text-sm leading-relaxed text-foreground sm:text-base ${
            compact ? "line-clamp-8" : ""
          }`}
        >
          &ldquo;{review.text}&rdquo;
        </blockquote>
        <div className="mt-4 flex items-center gap-2.5">
          <ReviewAvatar src={review.image} name={review.author} />
          <div className="min-w-0">
            <cite className="block truncate text-sm font-medium text-foreground not-italic">
              {review.author}
            </cite>
            {review.productHref ? (
              <Link
                href={review.productHref}
                className="text-xs text-muted-foreground underline underline-offset-2 hover:text-primary hover:no-underline"
              >
                {review.sourceLabel}
              </Link>
            ) : (
              <span className="text-xs text-muted-foreground">{review.sourceLabel}</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
